import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import nebulaTheme from '@nebula/theme';
import GlobalStyles from '@nebula/global-styles';
import { sendMobileErrorNotification, sendMobileLoadedNotification } from '@ecs-uk/web-common-mobile-view-helpers';

import usePageView from '../../../hooks/usePageView';
import useTargetOffer from '../../../hooks/useTargetOffer';
import useCurrentOffer from '../../../hooks/useCurrentOffer';
import { products } from '../../../constants';
import PaymentOrchestrator from '../../../components/PaymentOrchestrator';
import { trackSwitchRejected } from '../../../helpers/switch/analytics';
import trackStartUpsell from '../../../actions/upsellstarttrackerservice/trackStartUpsell';
import useSwitchLoadRequirements from '../../switch/migration/useSwitchLoadRequirements';
import ChangeBillingPeriodPage from '../../switch/migration/ChangeBillingPeriodPage';
import usePaymentPageLoadAnalytics from '../../../hooks/payment/usePaymentPageLoadAnalytics';
import { FAMILIES_NOT_REQUIRING_PAYMENT } from '../../../constants/payment';
import useFlexData from '../../../hooks/flexHooks/useFlexData';
import queryParams from '../../../constants/queryParams';
import splitsList from '../../../constants/splits';
import {
  getFamilyBillingPeriods,
  getFlexPlanByFamilyAndPeriod,
  getFlexUpgradePlan,
} from '../../../helpers/flex-helpers/findFlexPlanByFamily';
import resolveFlexBillingFrequency from '../../../helpers/flex-helpers/flexBillingFrequency';

const PAGE_NAME = 'ProdMoveSwitchPageMobile';

const WEBVIEW_SUPPORTED_UPGRADE_FAMILIES = [products.identity, products.credit_expert];

const MobileSwitchPage = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const currentOffer = useCurrentOffer();
  const { targetOffer, targetFamily, isSourcedFromUrl, isTrial } = useTargetOffer();
  const rawChangeBillingPeriod = new URLSearchParams(location.search ?? '').get(queryParams.billingPeriod);
  const changeBillingPeriod = ['monthly', 'annual'].includes(rawChangeBillingPeriod) ? rawChangeBillingPeriod : null;
  const isAnnualPricingEnabled = useSelector(state => state.split?.treatments?.[splitsList.annualPricing]);

  const isSupportedFamily = ![products.breach, products.breachPlus].includes(currentOffer?.family);
  const webviewSupported = isSupportedFamily && WEBVIEW_SUPPORTED_UPGRADE_FAMILIES.includes(targetFamily);

  usePageView(PAGE_NAME, {
    productChange: { switchingTo: targetFamily, isTrial, fromDeeplink: isSourcedFromUrl },
  });

  const onReject = () => {
    trackSwitchRejected();
    sendMobileErrorNotification();
  };

  const { switchDepsLoading, switchCanLoad } = useSwitchLoadRequirements(targetOffer, currentOffer, onReject);

  usePaymentPageLoadAnalytics(targetOffer, FAMILIES_NOT_REQUIRING_PAYMENT);

  const { isFlexUser, entitlements, products: flexProducts } = useFlexData();
  const termFrequency = entitlements?.termFrequency ?? entitlements?.plans?.[0]?.termFrequency;
  const capturedPlanPriceRef = useRef(null);
  const livePlanPrice = entitlements?.planPrice ?? entitlements?.plans?.[0]?.price;
  if (capturedPlanPriceRef.current == null && typeof livePlanPrice === 'number') {
    capturedPlanPriceRef.current = livePlanPrice;
  }
  const currentBillingPeriod = resolveFlexBillingFrequency({
    term: termFrequency,
    planCode: entitlements?.plans?.[0]?.planCode,
  });
  const { hasBothBillingPeriods } = getFamilyBillingPeriods(targetFamily, flexProducts?.eligiblePlans);
  const isSameBillingPeriodChange = Boolean(changeBillingPeriod) && currentBillingPeriod === changeBillingPeriod;
  const shouldRejectBillingPeriodChange =
    Boolean(changeBillingPeriod) &&
    (!isFlexUser ||
      !isAnnualPricingEnabled ||
      isSameBillingPeriodChange ||
      (Boolean(flexProducts) && !hasBothBillingPeriods));
  let upgradePlan = null;
  if (isFlexUser) {
    upgradePlan = changeBillingPeriod
      ? getFlexPlanByFamilyAndPeriod(
          targetFamily,
          changeBillingPeriod,
          flexProducts?.eligiblePlans,
          flexProducts?.isTrialEligible
        )
      : getFlexUpgradePlan(targetFamily, flexProducts?.eligiblePlans, flexProducts?.isTrialEligible);
  }
  const currentFlexSubscription = isFlexUser
    ? {
        family: entitlements?.family,
        planCode: entitlements?.plans?.[0]?.planCode,
        freeTrial: Boolean(entitlements?.plans?.[0]?.trialLength),
        ...(termFrequency == null ? {} : { termFrequency }),
        ...(typeof capturedPlanPriceRef.current === 'number' ? { price: capturedPlanPriceRef.current } : {}),
        billingFrequency: currentBillingPeriod,
      }
    : null;
  const resolvedIsTrial = isFlexUser ? (upgradePlan?.product?.trialLength ?? 0) > 0 : isTrial;
  const isBillingPeriodChange = Boolean(changeBillingPeriod);
  const billingPeriodChangeLoading = isBillingPeriodChange && !shouldRejectBillingPeriodChange && !upgradePlan;
  const hasLoadableTarget = isFlexUser ? upgradePlan : targetOffer;
  const canLoadSwitchOrBillingPeriodChange = isBillingPeriodChange || switchCanLoad;

  useEffect(() => {
    if (!isBillingPeriodChange) {
      dispatch(trackStartUpsell(targetFamily, isTrial));
    }
  }, [dispatch, targetFamily, isTrial, isBillingPeriodChange]);

  useEffect(() => {
    if (billingPeriodChangeLoading) {
      return;
    }

    if (
      hasLoadableTarget &&
      canLoadSwitchOrBillingPeriodChange &&
      webviewSupported &&
      !shouldRejectBillingPeriodChange
    ) {
      sendMobileLoadedNotification();
    } else {
      onReject();
    }
  }, [
    billingPeriodChangeLoading,
    hasLoadableTarget,
    canLoadSwitchOrBillingPeriodChange,
    webviewSupported,
    shouldRejectBillingPeriodChange,
  ]);

  if (
    switchDepsLoading ||
    !hasLoadableTarget ||
    !canLoadSwitchOrBillingPeriodChange ||
    !webviewSupported ||
    shouldRejectBillingPeriodChange
  ) {
    return null;
  }

  if (isBillingPeriodChange) {
    return (
      <ChangeBillingPeriodPage
        upgradePlan={upgradePlan}
        targetPeriod={changeBillingPeriod}
        isFlexFlow={isFlexUser}
        currentFlexSubscription={currentFlexSubscription}
        isWebview
      />
    );
  }

  return (
    <ThemeProvider theme={nebulaTheme}>
      <GlobalStyles />
      <PaymentOrchestrator
        upgradeOffer={isFlexUser ? null : targetOffer}
        upgradePlan={upgradePlan}
        currentFlexSubscription={currentFlexSubscription}
        isTrial={resolvedIsTrial}
        isWebview
        isFlexFlow={isFlexUser}
      />
    </ThemeProvider>
  );
};

export default MobileSwitchPage;
